import type { LegalPageKey } from '@astrologermatch/shared';

// Draft legal texts (brief §3 "Legal and required pages"). Drafted from the product rules and
// marked for operator review in OPERATOR_INPUTS.md (#8). Values in [square brackets] are
// placeholders the operator must confirm before go-live.

export const LEGAL_PLACEHOLDERS = {
  company: 'Cyntra360Hub (OPC) Private Limited',
  registeredAddress: '[Registered office address — to be confirmed by the operator]',
  supportEmail: 'admin@astrologermatch.com',
  grievanceOfficerName: '[Grievance Officer name — to be confirmed]',
  grievanceOfficerEmail: 'admin@astrologermatch.com',
  lastUpdated: '28 September 2026',
};

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LegalDoc {
  title: string;
  description: string;
  intro: string;
  sections: LegalSection[];
}

const P = LEGAL_PLACEHOLDERS;

export const LEGAL_DOCS: Record<LegalPageKey, LegalDoc> = {
  terms: {
    title: 'Terms of Service',
    description: 'The terms that apply when you use AstrologerMatch as a client or an astrologer.',
    intro: `AstrologerMatch (astrologermatch.com) is operated by ${P.company}, India ("we", "us"). By using the site you agree to these terms.`,
    sections: [
      {
        heading: 'What AstrologerMatch is',
        paragraphs: [
          'AstrologerMatch is a search and discovery service. An AI guide helps you describe what you are looking for and shows you astrologers who are registered with us. We do not provide astrology services, consultations, predictions or remedies ourselves.',
          'Any consultation is a private arrangement between you and the astrologer you choose. You contact and pay them directly. We are not a party to that arrangement and we are not responsible for the advice given, the fees charged, or the outcome.',
        ],
      },
      {
        heading: 'No guaranteed outcomes',
        paragraphs: [
          'Astrology is a matter of belief and interpretation. Nothing on AstrologerMatch, including AI-written text, ratings or certificates, is a promise of any result. Do not rely on astrology instead of qualified medical, legal, financial or psychological advice.',
        ],
      },
      {
        heading: 'Paid placements',
        paragraphs: [
          'Some results are paid placements. They are always labelled "Ad". Other results are ranked by reviews from real clients on AstrologerMatch. See the Advertiser Policy for how placements work.',
        ],
      },
      {
        heading: 'Reviews',
        paragraphs: [
          'Reviews are collected through one-time links that astrologers send to their own clients. Reviews must describe a genuine experience, be in your own words, and must not contain abusive, unlawful or personal information about others. We may hide reviews that break these rules, and we limit reviews per internet connection to prevent abuse.',
        ],
      },
      {
        heading: 'Astrologer accounts',
        paragraphs: [
          'Astrologers must give accurate information, verify their email and a business email on their own domain, and keep their profile truthful. Astrologers must not promise guaranteed outcomes to clients, must not impersonate others, and must comply with applicable law. We may suspend or remove accounts that break these terms.',
        ],
      },
      {
        heading: 'Acceptable use',
        paragraphs: ['You must not:'],
        bullets: [
          'try to break, overload, scrape or probe the site or its security;',
          'submit fake reviews, fake clicks or automated traffic;',
          'upload unlawful, infringing or harmful content;',
          'use the site to harass anyone or to collect other people’s personal data.',
        ],
      },
      {
        heading: 'Liability',
        paragraphs: [
          'The site is provided "as is". To the extent permitted by law, our total liability to an astrologer for any claim is limited to the amount they paid us in the three months before the claim, and we are not liable to clients for consultations arranged with astrologers.',
        ],
      },
      {
        heading: 'Changes and law',
        paragraphs: [
          'We may update these terms; the date below shows the latest version. These terms are governed by the laws of India, and courts in India have jurisdiction.',
          `Last updated: ${P.lastUpdated}.`,
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    description:
      'How AstrologerMatch collects and uses personal data, in line with India’s Digital Personal Data Protection Act, 2023.',
    intro: `This policy explains how ${P.company} ("we") processes personal data as a Data Fiduciary under the Digital Personal Data Protection Act, 2023 (DPDP Act).`,
    sections: [
      {
        heading: 'Clients using the AI search',
        paragraphs: [
          'You do not need an account. The text you type to the Rishi is sent to our AI provider only to work out your topic and location and to write a short explanation for each result. We do not store or log the content of your chat, and we do not keep it after your search is answered.',
          'We record billing facts only: which astrologers were shown, for which keyword, and whether you opened a profile or pressed a Call, WhatsApp or Website button. To bill each click fairly and stop fraud, the page keeps a random session number in your browser tab (not a cookie; it disappears when you close the tab), and we keep a keyed hash of your IP address for up to two days. These records do not contain your chat text or your identity.',
        ],
      },
      {
        heading: 'Clients leaving a review',
        paragraphs: [
          'When you publish a review we store the name you enter, your rating, your review text and the date. To enforce the one-review-per-connection rule we store a keyed, one-way hash of your IP address and a masked version (for example 49.36.12.x) visible only to our administrators.',
        ],
      },
      {
        heading: 'Astrologers',
        paragraphs: [
          'We store the details you give us to run your listing: name, practice, login email, business email, phone and WhatsApp numbers, city, country, profile text, photo, links, keywords and bids, wallet transactions, reviews, certificates and website audit results. Passwords are stored only as strong one-way hashes.',
          'Payments are processed by Razorpay. We receive payment status and method, never your full card or bank details.',
        ],
      },
      {
        heading: 'Admin-created ("unclaimed") astrologer profiles',
        paragraphs: [
          'Some profiles are created by our administrators from information that is already public (for example, a business listing or a website), before the astrologer has registered themselves. Such a profile is clearly marked "Unclaimed" and never shows the email address our administrators used to contact the astrologer.',
          'If this is your profile, you can claim it — proving you own the email on file with a one-time code — to take full control of it, or you can ask us to remove it. A removal request is verified the same way, or reviewed by an administrator if you no longer have access to that email, and we remove or hide the profile within a reasonable time. A removed profile stops appearing anywhere on the site, including search engines.',
        ],
      },
      {
        heading: 'Why we process data (purposes)',
        paragraphs: [],
        bullets: [
          'to match clients with astrologers and show public profiles;',
          'to verify astrologers and prevent fraud and abuse;',
          'to process payments, issue receipts and certificates, and keep accounting records required by law;',
          'to send service emails (verification links, codes, receipts);',
          'to keep the service secure and working.',
        ],
      },
      {
        heading: 'Service providers (Data Processors)',
        paragraphs: [
          'We use: our hosting provider (Contabo); the AI provider selected by our administrator (Google Gemini, OpenAI or Anthropic) for chat interpretation and short summaries; Google Places for map listings and review summaries; Razorpay for payments; our email provider for service emails; and Cloudflare for security and bot protection. Some of these providers process data outside India.',
        ],
      },
      {
        heading: 'Cookies',
        paragraphs: [
          'We use a single essential cookie to keep astrologers and administrators logged in. We do not use advertising or tracking cookies. Cloudflare Turnstile may set its own essential security cookie on forms.',
        ],
      },
      {
        heading: 'How long we keep data',
        paragraphs: [
          'Astrologer account data is kept while the account is open and deleted or anonymised within 90 days of closure, except records we must keep by law (such as payment and tax records). Reviews stay on the profile they were written for unless hidden or removed. Backups are kept for up to 5 weeks.',
        ],
      },
      {
        heading: 'Your rights',
        paragraphs: [
          'Under the DPDP Act you may ask for a summary of your personal data, ask us to correct, complete, update or erase it, withdraw consent, nominate another person to exercise your rights, and raise a grievance. Contact our Grievance Officer (see the Grievance Officer page). You may also approach the Data Protection Board of India.',
        ],
      },
      {
        heading: 'Children',
        paragraphs: [
          'AstrologerMatch is intended for adults. Astrologer accounts are for people aged 18 or over.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          `${P.company}, ${P.registeredAddress}. Email: ${P.supportEmail}.`,
          `Last updated: ${P.lastUpdated}.`,
        ],
      },
    ],
  },
  'advertiser-policy': {
    title: 'Advertiser Policy',
    description: 'How paid placements (keyword bids), the "Ad" label, wallets and refunds work.',
    intro:
      'Astrologers can promote their profile by bidding on keywords. This policy explains how that works.',
    sections: [
      {
        heading: 'How placements work',
        paragraphs: [
          'You choose a keyword, a location (worldwide/online, a country or a city) and a bid per click. When a client’s search matches your keyword and location, the highest bids whose wallet covers the bid are shown as "Top matches", labelled "Ad". Being shown is free. Your bid is charged from your wallet when the client clicks your card — "View profile & reviews", Call, WhatsApp or Website — at most once per client visit in 24 hours. Clicks we detect as automated, clicks on your own card while you are logged in on that browser, clicks from a visit that failed our bot check, and clicks above a daily limit per network address are not charged. If your wallet can’t cover the bid you are not shown.',
          'The "Highly rated by clients" list is ranked by reviews and ratings only. You cannot pay to appear there, and clicks there are free.',
          'Verified, listed astrologers can put a free rating badge on their own website (Certificate tab). It shows your current AstrologerMatch rating and review count, refreshes itself about once a week, and disappears if your listing is paused or suspended. It sets no cookies and does not track your visitors; we only count how often it is shown.',
        ],
      },
      {
        heading: 'The "Ad" label',
        paragraphs: ['Every paid placement is labelled "Ad". This label cannot be removed.'],
      },
      {
        heading: 'Eligibility',
        paragraphs: [
          'To be listed or to advertise you need a verified business email, a website that meets our minimum SEO score, and a completed AI review of your listing. Your account must not be suspended.',
        ],
      },
      {
        heading: 'Content rules',
        paragraphs: [
          'Your profile and messages must be truthful. You must not promise guaranteed outcomes, make medical or legal claims, or use fear to sell remedies. We may pause promotions or suspend accounts that break these rules.',
        ],
      },
      {
        heading: 'Wallet and refunds',
        paragraphs: [
          'Wallet top-ups are prepaid credit for clicks. Charges for clicks that have already happened are final. If you close your account, the unused wallet balance is refunded to the original payment method, less any payment-gateway fees that are not returned to us. See the Refund & Cancellation Policy.',
        ],
      },
      {
        heading: 'No guarantee of results',
        paragraphs: [
          'We do not guarantee any number of showings, clicks, contacts or clients. Results depend on client searches, competition and your profile.',
          `Last updated: ${P.lastUpdated}.`,
        ],
      },
    ],
  },
  'refund-policy': {
    title: 'Refund & Cancellation Policy',
    description: 'Refunds for wallet top-ups and rating certificates on AstrologerMatch.',
    intro: 'This policy covers payments astrologers make to AstrologerMatch. Clients never pay us.',
    sections: [
      {
        heading: 'Wallet top-ups',
        paragraphs: [
          'Unused wallet balance is refundable when you close your account. Email us from your login email and we will refund the unused balance to the original payment method within 7–10 working days of confirming the closure. Amounts already charged for clicks are not refundable.',
          'If a payment was taken but your wallet was not credited, contact us with the Razorpay payment ID and we will credit or refund it.',
          'Payment gateway/platform fees, currency conversion fees, bank charges and any other charges will be deducted at actuals before a refund is processed.',
        ],
      },
      {
        heading: 'Rating certificates',
        paragraphs: [
          'A certificate is a digital product delivered immediately after payment. Once issued it cannot be cancelled or refunded. If your payment succeeded but no certificate was issued, contact us and we will issue it or refund you in full.',
          'When your rating or number of reviews changes, an existing certificate becomes out of date and a new one needs a new payment. Certificates you already bought stay downloadable with their original date.',
        ],
      },
      {
        heading: 'Cancelling your account',
        paragraphs: [
          'You can ask us to close your account at any time. Your public page is removed and your unused wallet balance is refunded as above.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [`Email: ${P.supportEmail}.`, `Last updated: ${P.lastUpdated}.`],
      },
    ],
  },
  contact: {
    title: 'Contact us',
    description:
      'Contact AstrologerMatch for help with your astrologer listing, reviews, payments or the AI search — support email, company and grievance details.',
    intro: 'We are happy to help astrologers and clients with questions about the site.',
    sections: [
      {
        heading: 'Company',
        paragraphs: [`AstrologerMatch is a product of ${P.company}, India.`, P.registeredAddress],
      },
      {
        heading: 'Email',
        paragraphs: [
          `General and billing questions: ${P.supportEmail}.`,
          'We cannot book consultations or answer astrology questions — please contact the astrologer directly from their profile.',
        ],
      },
      {
        heading: 'Complaints',
        paragraphs: [
          'For complaints about personal data or content, see the Grievance Officer page.',
        ],
      },
    ],
  },
  grievance: {
    title: 'Grievance Officer',
    description:
      'Grievance Officer contact details under the IT Rules, 2021 and the DPDP Act, 2023.',
    intro:
      'In line with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021 and the Digital Personal Data Protection Act, 2023, you can raise a complaint with our Grievance Officer.',
    sections: [
      {
        heading: 'Grievance Officer',
        paragraphs: [
          `Name: ${P.grievanceOfficerName}`,
          `Email: ${P.grievanceOfficerEmail}`,
          `Address: ${P.company}, ${P.registeredAddress}`,
        ],
      },
      {
        heading: 'How complaints are handled',
        paragraphs: [
          'We acknowledge complaints within 24 hours and aim to resolve them within 15 days. Please include the page address (URL), what the problem is, and how we can reach you.',
        ],
      },
    ],
  },
};
